"use client";

import React, { useEffect, useState } from "react";
import Image from "next/image";
import {
  IoCloseOutline,
  IoChevronBack,
  IoChevronForward,
} from "react-icons/io5";
import styles from "./photo-preview-dialog.module.css";
import { getMedia, MediaItem } from "@/lib/media/shared";

// Swiper Imports
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Keyboard } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";

interface PhotoPreviewDialogProps {
  isOpen: boolean;
  onClose: () => void;
  photos: Record<string, MediaItem>;
  video?: MediaItem;
  initialIndex?: number;
}

export default function PhotoPreviewDialog({
  isOpen,
  onClose,
  photos,
  video,
  initialIndex = 0,
}: PhotoPreviewDialogProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  // 🚀 FIX: State to hold the actual DOM nodes for Swiper Navigation
  const [prevEl, setPrevEl] = useState<HTMLButtonElement | null>(null);
  const [nextEl, setNextEl] = useState<HTMLButtonElement | null>(null);

  const mediaArray = Object.values(photos || {});
  if (video) mediaArray.push(video);

  const totalItems = mediaArray.length;

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      setCurrentIndex(initialIndex);
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen, initialIndex]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || totalItems === 0) return null;

  return (
    <div className={styles.dialogOverlay}>
      <div className={styles.topBar}>
        <div className={styles.counter}>
          {currentIndex + 1} / {totalItems}
        </div>
        <button
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Close"
        >
          <IoCloseOutline className={styles.icon} />
        </button>
      </div>

      <div className={styles.contentArea}>
        {/* 🚀 FIX: Pass setPrevEl to the ref */}
        <button
          ref={setPrevEl}
          className={`${styles.navButton} ${styles.left}`}
          aria-label="Previous"
        >
          <IoChevronBack className={styles.icon} />
        </button>

        <Swiper
          modules={[Navigation, Keyboard]}
          initialSlide={initialIndex}
          loop={totalItems > 1}
          keyboard={{ enabled: true }}
          // 🚀 FIX: Pass the DOM nodes directly to Swiper
          navigation={{
            prevEl: prevEl,
            nextEl: nextEl,
          }}
          onSlideChange={(swiper) => setCurrentIndex(swiper.realIndex)}
          className={styles.swiperContainer}
        >
          {mediaArray.map((mediaRaw, index) => {
            const resolvedMedia = getMedia(mediaRaw, "lg");

            const isAdjacent =
              Math.abs(currentIndex - index) <= 1 ||
              (currentIndex === 0 && index === totalItems - 1) ||
              (currentIndex === totalItems - 1 && index === 0);

            return (
              <SwiperSlide key={index} className={styles.swiperSlideCustom}>
                {resolvedMedia ? (
                  resolvedMedia.type === "video" ? (
                    <div className={styles.videoWrapper}>
                      <video
                        src={resolvedMedia.src}
                        poster={resolvedMedia.preview}
                        controls
                        autoPlay={currentIndex === index}
                        className={styles.videoElement}
                        onTouchStart={(e) => e.stopPropagation()}
                        onTouchMove={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                      />
                    </div>
                  ) : (
                    <div className={styles.imageWrapper}>
                      <Image
                        src={resolvedMedia.src}
                        alt={`Preview ${index + 1}`}
                        fill
                        sizes="100vw"
                        quality={90}
                        priority={isAdjacent}
                        className={styles.mediaElement}
                      />
                    </div>
                  )
                ) : (
                  <div className={styles.errorText}>Media unvailable</div>
                )}
              </SwiperSlide>
            );
          })}
        </Swiper>

        {/* 🚀 FIX: Pass setNextEl to the ref */}
        <button
          ref={setNextEl}
          className={`${styles.navButton} ${styles.right}`}
          aria-label="Next"
        >
          <IoChevronForward className={styles.icon} />
        </button>
      </div>
    </div>
  );
}
